import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useParams, Link, Redirect } from "react-router-dom";
import { Modal } from "react-bootstrap";
import {
  FaBook,
  FaCheck,
  FaClipboardList,
  FaCopy,
  FaEdit,
  FaFolderOpen,
  FaGraduationCap,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function classLabel(item) {
  if (!item) return "Class";
  if (
    item.section_id === "No" ||
    item.section_id === "null" ||
    item.section_id === null ||
    !item.section_name
  ) {
    return item.class_name || "Class";
  }
  return `${item.class_name}: ${item.section_name}`;
}

//Created By Cursor
function normalizeSectionId(sectionId) {
  if (
    sectionId === "null" ||
    sectionId === null ||
    sectionId === undefined ||
    sectionId === ""
  ) {
    return "No";
  }
  return sectionId;
}

//Created By Cursor
function classKey(item) {
  if (!item) return "";
  return `${item.class_id}-${normalizeSectionId(item.section_id)}`;
}

const ExamGuideline = () => {
  const { exam_id, session_id, exam_name, exam_status } = useParams();
  const loggedIn = localStorage.getItem("token") !== null;

  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [guidelineLoading, setGuidelineLoading] = useState(false);

  const [EXAM_ID] = useState(exam_id || "");
  const [SESSION_ID] = useState(session_id || "");
  const [EXAM_NAME] = useState(decodeURIComponent(exam_name || ""));
  const [EXAM_STATUS] = useState(decodeURIComponent(exam_status || ""));

  const [mClassList, setMClassList] = useState([]);
  const [CLASS_SELECTED_POSITION, setClassSelectedPosition] = useState(0);
  const [CLASS_ID, setClassId] = useState("");
  const [SECTION_ID, setSectionId] = useState("");

  const [mExamGuideline, setMExamGuideline] = useState([]);
  const [inputGuideline, setInputGuideline] = useState("");

  const [editingGuideline, setEditingGuideline] = useState(null);
  const [editGuidelineText, setEditGuidelineText] = useState("");

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyingGuideline, setCopyingGuideline] = useState(false);
  const [selectedClassToCopy, setSelectedClassToCopy] = useState(null);

  const classPositionRef = useRef(0);

  //Created By Cursor
  const applyClassSelection = useCallback((classList, position) => {
    if (!classList.length || !classList[position]) {
      setClassId("");
      setSectionId("");
      setMExamGuideline([]);
      return;
    }

    const selected = classList[position];
    setClassId(selected.class_id || "");
    setSectionId(normalizeSectionId(selected.section_id));
    setMExamGuideline([]);
          setEditingGuideline(null);
          setEditGuidelineText("");
  }, []);

  //Created By Cursor
  const getExamGuideline = useCallback(async () => {
    if (!CLASS_ID || !SECTION_ID) return;

    setGuidelineLoading(true);
    const schoolCode = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(`${API_BASE()}getExamGuideline.php`, {
        params: {
          school_code: schoolCode,
          exam_id: EXAM_ID,
          session_id: SESSION_ID,
          class_id: CLASS_ID,
          section_id: SECTION_ID,
        },
      });

      if (response.data?.api_status === "OK") {
        setMExamGuideline(response.data.data || []);
        } else {
        setMExamGuideline([]);
      }
    } catch (error) {
      console.error("Error fetching exam guidelines:", error);
      setMExamGuideline([]);
    } finally {
      setGuidelineLoading(false);
    }
  }, [CLASS_ID, SECTION_ID, EXAM_ID, SESSION_ID]);

  //Created By Cursor
  const getExamParticipatingClass = useCallback(
    async ({ soft = false, preserveSelection = false } = {}) => {
      if (!EXAM_ID || !SESSION_ID) {
        setMClassList([]);
        setBootLoading(false);
        setRefreshing(false);
      return;
    }

      if (soft) setRefreshing(true);
      else setBootLoading(true);

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setBootLoading(false);
        setRefreshing(false);
      return;
    }

      try {
        const response = await axios.get(
          `${API_BASE()}getExamParticipatingClass.php`,
          {
            params: {
              school_code: schoolCode,
              exam_id: EXAM_ID,
              session_id: SESSION_ID,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          const classList = response.data.data || [];
          setMClassList(classList);
          if (!preserveSelection) {
            setClassSelectedPosition(0);
            classPositionRef.current = 0;
          }
        } else {
          setMClassList([]);
          myToast.notify_warning(
            response.data?.api_remark || "Could not load participating classes."
          );
        }
      } catch (error) {
        console.error("Error fetching participating classes:", error);
        setMClassList([]);
        myToast.notify_warning("Network error loading exam guidelines.");
      } finally {
        setBootLoading(false);
        setRefreshing(false);
      }
    },
    [EXAM_ID, SESSION_ID]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    getExamParticipatingClass();
  }, [getExamParticipatingClass]);

  useEffect(() => {
    classPositionRef.current = CLASS_SELECTED_POSITION;
    const position = Math.min(
      CLASS_SELECTED_POSITION,
      Math.max(mClassList.length - 1, 0)
    );
    applyClassSelection(mClassList, position);
  }, [CLASS_SELECTED_POSITION, mClassList, applyClassSelection]);

  useEffect(() => {
    if (!CLASS_ID || !SECTION_ID) return;
    getExamGuideline();
  }, [CLASS_ID, SECTION_ID, getExamGuideline]);

  const selectedClass = mClassList[CLASS_SELECTED_POSITION] || null;

  const otherClasses = useMemo(
    () =>
      mClassList.filter((_, index) => index !== CLASS_SELECTED_POSITION),
    [mClassList, CLASS_SELECTED_POSITION]
  );

  const hasOtherClasses = otherClasses.length > 0;

  const kpi = useMemo(
    () => ({
      classCount: mClassList.length,
      guidelineCount: mExamGuideline.length,
    }),
    [mClassList.length, mExamGuideline.length]
  );

  //Created By Cursor
  const addExamGuideline = useCallback(async () => {
    if (!inputGuideline.trim()) {
      myToast.notify_warning("Enter exam guideline.");
      return;
    }

    setGuidelineLoading(true);
    const schoolCode = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(`${API_BASE()}AddExamGuideline.php`, {
        params: {
          session_id: SESSION_ID,
          exam_id: EXAM_ID,
          class_id: CLASS_ID,
          section_id: SECTION_ID,
          guideline: inputGuideline.trim(),
          school_code: schoolCode,
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Exam guideline added successfully.");
        setInputGuideline("");
        getExamGuideline();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not add guideline."
        );
        setGuidelineLoading(false);
      }
    } catch (error) {
      console.error("Error adding guideline:", error);
      myToast.notify_error("Network error adding guideline.");
      setGuidelineLoading(false);
    }
  }, [
    inputGuideline,
    SESSION_ID,
    EXAM_ID,
    CLASS_ID,
    SECTION_ID,
    getExamGuideline,
  ]);

  //Created By Cursor
  const updateExamGuideline = useCallback(async () => {
    if (!editGuidelineText.trim()) {
      myToast.notify_warning("Enter exam guideline.");
      return;
    }

    setGuidelineLoading(true);
    const schoolCode = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(
        `${API_BASE()}UpdateExamGuideline.php`,
        {
          params: {
            guideline_id: editingGuideline,
            guideline: editGuidelineText.trim(),
            school_code: schoolCode,
          },
        }
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Exam guideline updated successfully.");
        setEditingGuideline(null);
        setEditGuidelineText("");
        getExamGuideline();
      } else {
        myToast.notify_error(
          response.data?.api_remark || "Error updating guideline."
        );
        setGuidelineLoading(false);
      }
    } catch (error) {
      console.error("Error updating guideline:", error);
      myToast.notify_error("Network error updating guideline.");
      setGuidelineLoading(false);
    }
  }, [editGuidelineText, editingGuideline, getExamGuideline]);

  //Created By Cursor
  const deleteExamGuideline = useCallback(
    async (guidelineId) => {
      setGuidelineLoading(true);
      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id;

      try {
        const response = await axios.get(
          `${API_BASE()}deleteExamGuideline.php`,
          {
            params: {
              session_id: SESSION_ID,
              school_code: schoolCode,
              exam_id: EXAM_ID,
              guideline_id: guidelineId,
              staff_id: staffId,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          myToast.notify_success("Exam guideline deleted successfully.");
          getExamGuideline();
        } else {
          myToast.notify_warning(
            response.data?.api_remark || "Could not delete guideline."
          );
          setGuidelineLoading(false);
        }
      } catch (error) {
        console.error("Error deleting guideline:", error);
        myToast.notify_error("Network error deleting guideline.");
        setGuidelineLoading(false);
      }
    },
    [SESSION_ID, EXAM_ID, getExamGuideline]
  );

  //Created By Cursor
  const copyExamGuideline = useCallback(async () => {
    if (!selectedClassToCopy) {
      myToast.notify_warning("Please select a class to copy from.");
      return;
    }

    const fromClassId = selectedClassToCopy.class_id;
    const fromSectionId = normalizeSectionId(selectedClassToCopy.section_id);

    if (fromClassId === CLASS_ID && fromSectionId === SECTION_ID) {
      myToast.notify_warning("Cannot copy from the same class.");
      return;
    }

    setCopyingGuideline(true);
    const schoolCode = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(`${API_BASE()}copyExamGuideline.php`, {
        params: {
          session_id: SESSION_ID,
          exam_id: EXAM_ID,
          from_class_id: fromClassId,
          from_section_id: fromSectionId,
          to_class_id: CLASS_ID,
          to_section_id: SECTION_ID,
          school_code: schoolCode,
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Exam guidelines copied successfully.");
        setShowCopyModal(false);
        setSelectedClassToCopy(null);
        getExamGuideline();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Failed to copy guidelines."
        );
      }
    } catch (error) {
      console.error("Error copying guidelines:", error);
      myToast.notify_error("Failed to copy guidelines.");
    } finally {
      setCopyingGuideline(false);
    }
  }, [
    selectedClassToCopy,
    CLASS_ID,
    SECTION_ID,
    SESSION_ID,
    EXAM_ID,
    getExamGuideline,
  ]);

  //Created By Cursor
  const refreshSoft = useCallback(() => {
    getExamParticipatingClass({ soft: true, preserveSelection: true });
    getExamGuideline();
  }, [getExamParticipatingClass, getExamGuideline]);

  //Created By Cursor
  const openCopyModal = useCallback(() => {
    setSelectedClassToCopy(null);
    setShowCopyModal(true);
  }, []);

  //Created By Cursor
  const closeCopyModal = useCallback(() => {
    if (copyingGuideline) return;
    setShowCopyModal(false);
    setSelectedClassToCopy(null);
  }, [copyingGuideline]);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (bootLoading) return <MyLoader />;

  const detailsPath = `/AdminMain/ExamDetails/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(
    EXAM_NAME || ""
  )}/${encodeURIComponent(EXAM_STATUS || "")}`;

  return (
    <div
      className="content payroll-page sm-page eg-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
            <div className="leftside-content-header">
              <ul className="breadcrumbs">
                <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Exam Guideline</span>
                </li>
              </ul>
            </div>
        <div className="rightside-content-header">
          <BackButton />
          </div>
                  </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
              </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Exam Guidelines</h1>
              <p className="sm-hero__sub">
                {EXAM_NAME || "Exam"} · Manage instructions per class
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {EXAM_STATUS ? (
              <span className="eg-status-pill" title="Exam status">
                {EXAM_STATUS}
              </span>
            ) : null}
            <button
              type="button"
              onClick={refreshSoft}
              disabled={refreshing || guidelineLoading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to={detailsPath} className="eg-hero-link">
              Exam Details
            </Link>
            <Link to="/AdminMain/ExamList" className="eg-hero-link">
              Exam List
            </Link>
          </div>
        </header>

        <div className="eg-content">
          <div className="eg-kpi-row">
            <span className="eg-kpi">
              <FaUsers aria-hidden />
              {kpi.classCount} classes
            </span>
            <span className="eg-kpi">
              <FaBook aria-hidden />
              {kpi.guidelineCount} guidelines
                  </span>
                </div>

          <section className="eg-section-card" aria-label="Participating classes">
            <div className="eg-section-card__header">
              <h2 className="eg-section-card__title">Participating Classes</h2>
              <span className="eg-section-card__count">{mClassList.length}</span>
            </div>
            <div className="eg-section-card__body">
              {mClassList.length === 0 ? (
                <div className="eg-empty">
                  <p className="eg-empty__title">No participating classes</p>
                  <p className="eg-empty__hint">
                    Add classes from Exam Details first.
                  </p>
                </div>
              ) : (
                <div className="eg-chip-grid">
                  {mClassList.map((classItem, index) => (
                    <button
                      key={`${classItem.class_id}-${classItem.section_id}-${index}`}
                      type="button"
                      className={`eg-chip${
                        CLASS_SELECTED_POSITION === index ? " is-active" : ""
                      }`}
                      onClick={() => setClassSelectedPosition(index)}
                    >
                      {classLabel(classItem)}
                    </button>
              ))}
              </div>
              )}
            </div>
          </section>

          <section className="eg-section-card" aria-label="Add guideline">
            <div className="eg-section-card__header">
              <h2 className="eg-section-card__title">
                Add Guideline · {classLabel(selectedClass)}
              </h2>
              <div className="eg-header-actions">
                <button
                  type="button"
                  className="eg-header-btn"
                  disabled={!hasOtherClasses}
                  onClick={openCopyModal}
                  title={
                    hasOtherClasses
                      ? "Copy guidelines from another class"
                      : "No other classes available"
                  }
                >
                  <FaCopy aria-hidden /> Copy From Class
                </button>
              </div>
            </div>
            <div className="eg-section-card__body">
              <div className="eg-add-row">
                <label className="eg-field eg-field--grow">
                  <span className="eg-field__label">Guideline Text</span>
                  <textarea
                    className="eg-textarea"
                    value={inputGuideline}
                    onChange={(e) => setInputGuideline(e.target.value)}
                    placeholder="Enter exam guideline here..."
                    rows={4}
                  />
                </label>
                <div className="eg-add-action">
                <button
                    type="button"
                    className="eg-add-btn"
                    onClick={addExamGuideline}
                    disabled={guidelineLoading || !CLASS_ID}
                  >
                    <FaPlus aria-hidden /> Add Guideline
                </button>
              </div>
            </div>
            </div>
          </section>

          <section className="eg-section-card" aria-label="Guidelines preview">
            <div className="eg-section-card__header">
              <h2 className="eg-section-card__title">Guidelines Preview</h2>
              <span className="eg-section-card__count">
                {mExamGuideline.length}
              </span>
            </div>
            <div className="eg-section-card__body">
              {guidelineLoading && mExamGuideline.length === 0 ? (
                <div className="eg-loading-inline">
                <MyLoader />
              </div>
              ) : mExamGuideline.length === 0 ? (
                <div className="eg-empty eg-empty--dashed">
                  <FaClipboardList className="eg-empty__icon" aria-hidden />
                  <p className="eg-empty__title">No guidelines added yet</p>
                  <p className="eg-empty__hint">
                    Add your first exam guideline using the form above.
                  </p>
                      </div>
              ) : (
                <div
                  className={`eg-table-wrap${
                    guidelineLoading ? " is-busy" : ""
                  }`}
                >
                  <table className="eg-table">
                        <thead>
                      <tr>
                        <th>#</th>
                        <th>Guideline</th>
                        <th className="eg-col-action">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                      {mExamGuideline.map((item, index) => {
                        const isEditing =
                          editingGuideline === item.guideline_id;
                        return (
                          <tr
                            key={item.guideline_id || index}
                            className={index % 2 === 1 ? "is-even" : ""}
                          >
                            <td className="eg-col-num">{index + 1}</td>
                            <td>
                              {isEditing ? (
                                  <textarea
                                  className="eg-textarea eg-textarea--inline"
                                    value={editGuidelineText}
                                  onChange={(e) =>
                                    setEditGuidelineText(e.target.value)
                                  }
                                  rows={3}
                                  />
                                ) : (
                                <span className="eg-guideline-text">
                                  {item.guideline}
                                </span>
                                )}
                              </td>
                            <td className="eg-col-action">
                              {isEditing ? (
                                <div className="eg-row-actions">
                                    <button
                                    type="button"
                                    className="eg-action-btn eg-action-btn--save"
                                      onClick={updateExamGuideline}
                                  >
                                    <FaCheck aria-hidden /> Save
                                    </button>
                                    <button
                                    type="button"
                                    className="eg-action-btn eg-action-btn--cancel"
                                    onClick={() => {
                                      setEditingGuideline(null);
                                      setEditGuidelineText("");
                                    }}
                                  >
                                    <FaTimes aria-hidden /> Cancel
                                    </button>
                                  </div>
                                ) : (
                                <div className="eg-row-actions">
                                    <button
                                    type="button"
                                    className="eg-action-btn eg-action-btn--edit"
                                    onClick={() => {
                                      setEditingGuideline(item.guideline_id);
                                      setEditGuidelineText(item.guideline);
                                    }}
                                  >
                                    <FaEdit aria-hidden /> Edit
                                    </button>
                                    <button
                                    type="button"
                                    className="eg-action-btn eg-action-btn--delete"
                                    onClick={() =>
                                      deleteExamGuideline(item.guideline_id)
                                    }
                                  >
                                    <FaTrash aria-hidden /> Delete
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                        );
                      })}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </div>
          </section>
        </div>

        <Modal
          show={showCopyModal}
          onHide={closeCopyModal}
          centered
          size="lg"
          backdrop={!copyingGuideline}
          keyboard={!copyingGuideline}
          dialogClassName="eg-copy-modal"
          contentClassName="eg-copy-modal__content"
        >
          <Modal.Header closeButton={!copyingGuideline} className="eg-copy-modal__header">
            <Modal.Title className="eg-copy-modal__title">
              <span className="eg-copy-modal__title-icon" aria-hidden>
                <FaCopy />
              </span>
              <span>
                <span className="eg-copy-modal__title-main">Copy Guidelines</span>
                <span className="eg-copy-modal__title-sub">
                  Import from another participating class
                </span>
              </span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="eg-copy-modal__body">
            <div className="eg-copy-target">
              <span className="eg-copy-target__icon" aria-hidden>
                <FaGraduationCap />
              </span>
              <div className="min-w-0">
                <span className="eg-copy-target__label">Copying into</span>
                <strong className="eg-copy-target__name">
                  {classLabel(selectedClass)}
                </strong>
              </div>
            </div>

            <div className="eg-copy-source-head">
              <p className="eg-copy-source-head__label">Choose source class</p>
              <span className="eg-copy-source-head__count">
                {otherClasses.length} available
              </span>
            </div>

            {copyingGuideline ? (
              <div className="eg-copy-loading">
                <MyLoader />
                <p>Copying guidelines...</p>
              </div>
            ) : otherClasses.length === 0 ? (
              <div className="eg-empty eg-empty--dashed">
                <p className="eg-empty__title">No other classes available</p>
                <p className="eg-empty__hint">
                  Add more participating classes to copy guidelines.
                </p>
              </div>
            ) : (
              <div className="eg-copy-grid">
                {otherClasses.map((item, index) => {
                    const isSelected =
                    selectedClassToCopy &&
                    classKey(selectedClassToCopy) === classKey(item);

                    return (
                    <button
                        key={`${item.class_id}-${item.section_id}-${index}`}
                      type="button"
                      className={`eg-copy-tile${
                        isSelected ? " is-selected" : ""
                      }`}
                        onClick={() => setSelectedClassToCopy(item)}
                      >
                        {isSelected ? (
                        <span className="eg-copy-tile__check" aria-hidden>
                          <FaCheck />
                          </span>
                        ) : null}
                      <span className="eg-copy-tile__icon" aria-hidden>
                        <FaFolderOpen />
                      </span>
                      <span className="eg-copy-tile__name" title={classLabel(item)}>
                        {classLabel(item)}
                      </span>
                    </button>
                    );
                  })}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="eg-copy-modal__footer">
            <div className="eg-copy-selected">
              <span className="eg-copy-selected__label">Selected source</span>
              <strong className="eg-copy-selected__name">
                {selectedClassToCopy
                  ? classLabel(selectedClassToCopy)
                  : "None selected"}
              </strong>
              </div>
            <div className="eg-copy-footer-actions">
              <button
                type="button"
                className="eg-copy-btn eg-copy-btn--ghost"
                onClick={closeCopyModal}
                disabled={copyingGuideline}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eg-copy-btn eg-copy-btn--primary"
                onClick={copyExamGuideline}
                disabled={copyingGuideline || !selectedClassToCopy}
              >
                <FaCopy aria-hidden /> Copy Guidelines
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ExamGuideline;
